import type {
  Pageable,
  PageResponse,
} from '@/entities/user/api/user.api';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

/**
 * 페이지네이션 목록 API를 React Query로 조회할 때 쓰는 공통 옵션.
 * queryKey에 search + page를 넣어 필터·페이지 변경 시 자동 refetch 한다.
 */
export interface PaginatedQueryParams<TSearch> {
  search: TSearch;
  pageable: Pageable;
}

type PaginatedQueryFn<TSearch, TItem> = (
  params: PaginatedQueryParams<TSearch>,
) => Promise<PageResponse<TItem>>;

interface UsePaginatedQueryOptions<TSearch, TItem, TError = Error>
  extends Omit<
    UseQueryOptions<PageResponse<TItem>, TError>,
    'queryKey' | 'queryFn'
  > {
  /** React Query 캐시 키 prefix. 예: ['hospitals', 'list'] */
  queryKeyPrefix: readonly unknown[];
  search: TSearch;
  pageable: Pageable;
  queryFn: PaginatedQueryFn<TSearch, TItem>;
}

/**
 * 목록 화면(병의원·약국·제약사 등)에서 반복되는 페이지네이션 조회 패턴.
 *
 * 각 도메인 훅(useHospitalList 등)은 이 훅을 감싸고,
 * 페이지 컴포넌트는 도메인 훅만 import 한다.
 */
export const usePaginatedQuery = <TSearch, TItem, TError = Error>({
  queryKeyPrefix,
  search,
  pageable,
  queryFn,
  ...options
}: UsePaginatedQueryOptions<TSearch, TItem, TError>) =>
  useQuery({
    queryKey: [...queryKeyPrefix, search, pageable],
    queryFn: () => queryFn({ search, pageable }),
    ...options,
  });
